import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Upload, 
  FileText, 
  Image, 
  File,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  Sparkles,
  AlertCircle,
  Shield,
  Info,
  ChevronDown,
  ChevronUp,
  Plus,
  X
} from 'lucide-react';
import { serenityClient } from '../services/serenityClient';
import type { Debtor, Document, DocumentType } from '../types';
import { REQUIRED_DOCUMENTS, RequiredDocumentConfig } from '../data/collectionRequirements';
import { RequiredDocumentSlot } from '../components/RequiredDocumentSlot';
import { FileUploader } from '../components/FileUploader';
import { classifyDocument, formatFileSize } from '../utils/fileHelpers';

interface DocumentsPageProps {
  debtor: Debtor;
}

interface UploadProgress {
  step: string;
  progress: number;
}

interface UploadingSlot {
  documentType: DocumentType;
  progress: UploadProgress;
}

// Bulk upload tracking
interface BulkUploadFile {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'classifying' | 'saving' | 'complete' | 'error';
  progress: number;
  classifiedAs?: DocumentType;
  confidence?: number;
  error?: string;
}

const DOCUMENT_TYPES = [
  { value: 'income_statement', label: 'Income Statement' },
  { value: 'bank_statement', label: 'Bank Statement' },
  { value: 'employment_letter', label: 'Employment Letter' },
  { value: 'identity_document', label: 'Identity Document' },
  { value: 'proof_of_address', label: 'Proof of Address' },
  { value: 'hardship_declaration', label: 'Hardship Declaration' },
  { value: 'payment_proof', label: 'Payment Proof' },
  { value: 'medical_certificate', label: 'Medical Certificate' },
  { value: 'unemployment_certificate', label: 'Unemployment Certificate' },
  { value: 'other', label: 'Other Document' },
];

// Classify document type based on filename (local fallback)
function classifyByFilename(fileName: string): { type: string; confidence: number } {
  const lowerName = fileName.toLowerCase();
  
  const patterns: Array<{ keywords: string[]; type: string }> = [
    { keywords: ['income', 'salary', 'payslip', 'pay stub', 'nomina', 'sueldo'], type: 'income_statement' },
    { keywords: ['bank', 'statement', 'account', 'extracto', 'cuenta'], type: 'bank_statement' },
    { keywords: ['employment', 'employer', 'trabajo', 'empleo', 'contract'], type: 'employment_letter' },
    { keywords: ['passport', 'dni', 'id', 'identity', 'cedula', 'license'], type: 'identity_document' },
    { keywords: ['address', 'utility', 'factura', 'domicilio', 'proof', 'residence'], type: 'proof_of_address' },
    { keywords: ['hardship', 'dificultad', 'declaration', 'carta'], type: 'hardship_declaration' },
    { keywords: ['payment', 'receipt', 'pago', 'recibo', 'transfer'], type: 'payment_proof' },
    { keywords: ['medical', 'doctor', 'hospital', 'medico', 'health'], type: 'medical_certificate' },
    { keywords: ['unemployment', 'desempleo', 'paro', 'jobless'], type: 'unemployment_certificate' },
  ];

  for (const pattern of patterns) {
    if (pattern.keywords.some(kw => lowerName.includes(kw))) {
      return { type: pattern.type, confidence: 0.75 };
    }
  }

  return { type: 'other', confidence: 0.5 };
}

export default function DocumentsPage({ debtor }: DocumentsPageProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadingSlot, setUploadingSlot] = useState<UploadingSlot | null>(null);
  const [showConditionalDocs, setShowConditionalDocs] = useState(false);
  const [showOtherDocuments, setShowOtherDocuments] = useState(false);
  
  // Bulk upload state
  const [bulkUploads, setBulkUploads] = useState<BulkUploadFile[]>([]);
  const [showBulkUploader, setShowBulkUploader] = useState(true);
  
  // Hidden file input ref
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingUploadType, setPendingUploadType] = useState<DocumentType | null>(null);

  useEffect(() => {
    loadDocuments();
  }, [debtor.debtor_id]);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const result = await serenityClient.queryData<Document>(
        `SELECT * FROM Documents WHERE debtor_id = '${debtor.debtor_id}' AND (status IS NULL OR status != 'deleted') ORDER BY uploaded_at DESC`
      );
      setDocuments(result);
      
      // If we have any conditional documents uploaded, show that section
      const conditionalTypes = ['hardship_declaration', 'medical_certificate', 'unemployment_certificate', 'payment_proof'];
      const hasConditionalDocs = result.some(d => conditionalTypes.includes(d.document_type || ''));
      if (hasConditionalDocs) setShowConditionalDocs(true);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Get document for a specific type/slot
  const getDocumentForType = (type: DocumentType): Document | null => {
    return documents.find(d => d.document_type === type) || null;
  };
  
  // Get "other" documents (not matching any slot)
  const getOtherDocuments = (): Document[] => {
    const slotTypes = REQUIRED_DOCUMENTS.map(r => r.type);
    return documents.filter(d => !slotTypes.includes(d.document_type as DocumentType));
  };

  const triggerUpload = (docType: DocumentType) => {
    setPendingUploadType(docType);
    fileInputRef.current?.click();
  };

  // Handle bulk file drop/upload
  const handleBulkUpload = useCallback(async (files: File[]) => {
    // Create tracking entries for each file
    const newUploads: BulkUploadFile[] = files.map(file => ({
      id: `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      status: 'pending' as const,
      progress: 0,
    }));

    setBulkUploads(prev => [...prev, ...newUploads]);

    // Process each file sequentially
    for (const upload of newUploads) {
      await processBulkFile(upload);
    }
  }, [debtor]);

  const processBulkFile = async (upload: BulkUploadFile) => {
    const updateUpload = (updates: Partial<BulkUploadFile>) => {
      setBulkUploads(prev => prev.map(u => 
        u.id === upload.id ? { ...u, ...updates } : u
      ));
    };

    try {
      // Step 1: Classify by filename
      updateUpload({ status: 'classifying', progress: 10 });
      const { type: classifiedType, confidence } = classifyDocument(upload.file.name);
      updateUpload({ classifiedAs: classifiedType, confidence, progress: 20 });

      // Step 2: Upload to Volatile Knowledge
      updateUpload({ status: 'uploading', progress: 30 });
      
      let volatileKnowledgeId: string | undefined;
      try {
        const vkResult = await serenityClient.uploadVolatileKnowledge(upload.file);
        volatileKnowledgeId = vkResult.id;
        updateUpload({ progress: 50 });
      } catch (vkError) {
        console.warn('Volatile Knowledge upload failed:', vkError);
      }

      // Step 3: Try AI classification to improve accuracy
      let finalType = classifiedType;
      let finalConfidence = confidence;
      if (volatileKnowledgeId) {
        try {
          const documentId = `DOC-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`;
          const classResult = await serenityClient.classifyDocument(
            debtor.debtor_id,
            documentId,
            classifiedType,
            volatileKnowledgeId
          );
          finalConfidence = Math.max(finalConfidence, classResult.confidence);
        } catch {
          // Keep local classification
        }
      }
      updateUpload({ classifiedAs: finalType, confidence: finalConfidence, progress: 60 });

      // Step 4: Get case_id
      updateUpload({ status: 'saving', progress: 70 });
      let caseId = '';
      try {
        const cases = await serenityClient.queryData<{ case_id: string }>(
          `SELECT case_id FROM Cases WHERE debtor_id = '${debtor.debtor_id}' LIMIT 1`
        );
        caseId = cases[0]?.case_id || '';
      } catch {
        console.warn('Could not fetch case_id');
      }

      // Step 5: Store document using correct schema
      const documentId = `DOC-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
      const now = new Date().toISOString();
      
      // Get public URL from VK
      const publicUrl = volatileKnowledgeId 
        ? `https://api.serenitystar.ai/api/v2/file/download/public/${volatileKnowledgeId}`
        : '';
      
      await serenityClient.insertData('Documents', {
        document_id: documentId,
        debtor_id: debtor.debtor_id,
        case_id: caseId || null,
        document_type: finalType,
        title: upload.file.name.replace(/\.[^.]+$/, ''),
        file_name: upload.file.name,
        mime_type: upload.file.type || 'application/octet-stream',
        file_size: upload.file.size,
        uploaded_by: debtor.debtor_id,
        uploaded_at: now,
        status: 'pending',
        notes: volatileKnowledgeId ? `VK ID: ${volatileKnowledgeId}` : null,
      });

      updateUpload({ progress: 90 });

      updateUpload({ status: 'complete', progress: 100 });

      // Reload documents
      loadDocuments();

    } catch (error) {
      console.error('Bulk upload failed for file:', upload.file.name, error);
      updateUpload({ 
        status: 'error', 
        error: error instanceof Error ? error.message : 'Error uploading document'
      });
    }
  };

  const clearCompletedUploads = () => {
    setBulkUploads(prev => prev.filter(u => u.status !== 'complete'));
  };

  const removeBulkUpload = (id: string) => {
    setBulkUploads(prev => prev.filter(u => u.id !== id));
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !pendingUploadType) return;
    
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }
    
    await uploadDocument(file, pendingUploadType);
    setPendingUploadType(null);
  };

  const uploadDocument = async (file: File, docType: DocumentType) => {
    setUploadingSlot({ documentType: docType, progress: { step: 'Preparing...', progress: 10 } });
    
    try {
      // Step 1: Upload to Volatile Knowledge
      setUploadingSlot({ documentType: docType, progress: { step: 'Uploading...', progress: 20 } });
      
      let volatileKnowledgeId: string | undefined;
      try {
        const vkResult = await serenityClient.uploadVolatileKnowledge(file);
        volatileKnowledgeId = vkResult.id;
        setUploadingSlot({ documentType: docType, progress: { step: 'AI analyzing...', progress: 40 } });
      } catch (vkError) {
        console.warn('Volatile Knowledge upload failed:', vkError);
      }

      // Step 2: Create document record
      const documentId = `DOC-${Date.now()}`;
      
      // Step 3: Try AI classification (optional - can fail gracefully)
      if (volatileKnowledgeId) {
        try {
          setUploadingSlot({ documentType: docType, progress: { step: 'Classifying document...', progress: 50 } });
          // Wait for VK to process the file
          await serenityClient.waitForVolatileKnowledge(volatileKnowledgeId);
          setUploadingSlot({ documentType: docType, progress: { step: 'AI analyzing...', progress: 60 } });
        } catch (classifyError) {
          console.warn('AI classification failed:', classifyError);
        }
      }

      setUploadingSlot({ documentType: docType, progress: { step: 'Processing...', progress: 70 } });

      // Step 4: Get a case_id for this debtor (required field)
      let caseId = '';
      try {
        const cases = await serenityClient.queryData<{ case_id: string }>(
          `SELECT case_id FROM Cases WHERE debtor_id = '${debtor.debtor_id}' LIMIT 1`
        );
        caseId = cases[0]?.case_id || '';
      } catch {
        console.warn('Could not fetch case_id');
      }

      // Step 5: Store document using correct schema
      setUploadingSlot({ documentType: docType, progress: { step: 'Saving...', progress: 85 } });
      
      const now = new Date().toISOString();
      
      await serenityClient.insertData('Documents', {
        document_id: documentId,
        debtor_id: debtor.debtor_id,
        case_id: caseId || null,
        document_type: docType,
        title: file.name.replace(/\.[^.]+$/, ''),
        file_name: file.name,
        mime_type: file.type || 'application/octet-stream',
        file_size: file.size,
        uploaded_by: debtor.debtor_id,
        uploaded_at: now,
        status: 'pending',
        notes: volatileKnowledgeId ? `VK ID: ${volatileKnowledgeId}` : null,
      });

      setUploadingSlot({ documentType: docType, progress: { step: 'Complete!', progress: 100 } });
      
      // Reload after short delay
      setTimeout(() => {
        setUploadingSlot(null);
        loadDocuments();
      }, 1000);
      
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadingSlot(null);
      alert('Failed to upload document. Please try again.');
    }
  };

  const handleRemoveDocument = async (doc: Document) => {
    if (!confirm('Are you sure you want to remove this document?')) return;
    
    try {
      // Mark as deleted by updating status (soft delete)
      // The document will be filtered out on next load
      await serenityClient.updateData('Documents', {
        status: 'deleted',
        deleted_at: new Date().toISOString(),
      }, `document_id = '${doc.document_id}'`);
      
      // Remove from local state immediately
      setDocuments(prev => prev.filter(d => d.document_id !== doc.document_id));
    } catch (error) {
      console.error('Failed to remove document:', error);
      alert('Failed to remove document.');
    }
  };

  // Calculate progress
  const requiredDocs = REQUIRED_DOCUMENTS.filter(r => r.required);
  const uploadedRequiredCount = requiredDocs.filter(r => getDocumentForType(r.type)).length;
  const progressPercent = Math.round((uploadedRequiredCount / requiredDocs.length) * 100);

  // Separate required from conditional docs
  const requiredDocConfigs = REQUIRED_DOCUMENTS.filter(r => r.required);
  const conditionalDocConfigs = REQUIRED_DOCUMENTS.filter(r => !r.required);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        className="hidden"
      />

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link
              to="/dashboard"
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Document Center</h1>
                <p className="text-xs text-gray-500">Upload documents to support your case</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Bulk Upload Zone */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <Upload className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-900">Upload Documents</h2>
                    <p className="text-sm text-gray-500">Drag multiple files - they will be classified automatically</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <span className="text-xs text-gray-500">AI Auto-Classification</span>
                </div>
              </div>
              
              <FileUploader 
                onUpload={handleBulkUpload}
                multiple={true}
                maxSizeMB={10}
                disabled={bulkUploads.some(u => u.status === 'uploading' || u.status === 'classifying' || u.status === 'saving')}
              />

              {/* Bulk Upload Progress */}
              {bulkUploads.length > 0 && (
                <div className="mt-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Files in progress ({bulkUploads.filter(u => u.status === 'complete').length}/{bulkUploads.length})
                    </span>
                    {bulkUploads.some(u => u.status === 'complete') && (
                      <button
                        onClick={clearCompletedUploads}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        Clear completed
                      </button>
                    )}
                  </div>
                  
                  {bulkUploads.map(upload => (
                    <div 
                      key={upload.id}
                      className={`p-3 rounded-lg border ${
                        upload.status === 'error' 
                          ? 'bg-red-50 border-red-200' 
                          : upload.status === 'complete'
                          ? 'bg-green-50 border-green-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {/* File icon */}
                        <div className={`w-8 h-8 rounded flex items-center justify-center ${
                          upload.status === 'error' ? 'bg-red-100' :
                          upload.status === 'complete' ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          {upload.status === 'complete' ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : upload.status === 'error' ? (
                            <XCircle className="w-4 h-4 text-red-600" />
                          ) : (
                            <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                          )}
                        </div>
                        
                        {/* File info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{upload.file.name}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>{formatFileSize(upload.file.size)}</span>
                            {upload.classifiedAs && (
                              <>
                                <span>•</span>
                                <span className="text-blue-600">
                                  {DOCUMENT_TYPES.find(d => d.value === upload.classifiedAs)?.label || upload.classifiedAs}
                                </span>
                                {upload.confidence && (
                                  <span className="text-gray-400">({Math.round(upload.confidence * 100)}%)</span>
                                )}
                              </>
                            )}
                          </div>
                          {upload.error && (
                            <p className="text-xs text-red-600 mt-1">{upload.error}</p>
                          )}
                        </div>
                        
                        {/* Status/Actions */}
                        <div className="flex items-center gap-2">
                          {upload.status !== 'complete' && upload.status !== 'error' && (
                            <span className="text-xs text-gray-500">
                              {upload.status === 'classifying' && 'Classifying...'}
                              {upload.status === 'uploading' && 'Uploading...'}
                              {upload.status === 'saving' && 'Saving...'}
                              {upload.status === 'pending' && 'Queued...'}
                            </span>
                          )}
                          {(upload.status === 'complete' || upload.status === 'error') && (
                            <button
                              onClick={() => removeBulkUpload(upload.id)}
                              className="p-1 text-gray-400 hover:text-gray-600 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Progress bar */}
                      {upload.status !== 'complete' && upload.status !== 'error' && (
                        <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-600 transition-all duration-300"
                            style={{ width: `${upload.progress}%` }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Progress Overview */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-900">Document Verification</h2>
                    <p className="text-sm text-gray-500">{uploadedRequiredCount} of {requiredDocs.length} required documents uploaded</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-2xl font-bold ${progressPercent === 100 ? 'text-green-600' : 'text-blue-600'}`}>
                    {progressPercent}%
                  </span>
                  <p className="text-xs text-gray-500">Complete</p>
                </div>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-500 ${progressPercent === 100 ? 'bg-green-500' : 'bg-blue-600'}`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              {progressPercent === 100 && (
                <div className="mt-3 flex items-center gap-2 text-green-600 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  All required documents uploaded! Our team will review them shortly.
                </div>
              )}
            </div>

            {/* Required Documents Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Required Documents</h3>
                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                  {requiredDocs.length - uploadedRequiredCount} remaining
                </span>
              </div>
              <div className="space-y-3">
                {requiredDocConfigs.map(config => {
                  const doc = getDocumentForType(config.type);
                  const isCurrentlyUploading = uploadingSlot?.documentType === config.type;
                  return (
                    <RequiredDocumentSlot
                      key={config.type}
                      config={config}
                      document={doc}
                      onUpload={() => triggerUpload(config.type)}
                      onReplace={() => triggerUpload(config.type)}
                      onRemove={() => doc && handleRemoveDocument(doc)}
                      isUploading={isCurrentlyUploading}
                      uploadProgress={isCurrentlyUploading ? uploadingSlot?.progress : undefined}
                    />
                  );
                })}
              </div>
            </div>

            {/* Conditional/Supporting Documents Section */}
            <div>
              <button
                onClick={() => setShowConditionalDocs(!showConditionalDocs)}
                className="flex items-center gap-2 text-lg font-semibold text-gray-900 hover:text-gray-700 mb-4"
              >
                <span>Supporting Documents</span>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Optional</span>
                {showConditionalDocs ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              {showConditionalDocs && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-500 mb-4">
                    Upload these documents if they apply to your situation (e.g., hardship claims, medical circumstances).
                  </p>
                  {conditionalDocConfigs.map(config => {
                    const doc = getDocumentForType(config.type);
                    const isCurrentlyUploading = uploadingSlot?.documentType === config.type;
                    return (
                      <RequiredDocumentSlot
                        key={config.type}
                        config={config}
                        document={doc}
                        onUpload={() => triggerUpload(config.type)}
                        onReplace={() => triggerUpload(config.type)}
                        onRemove={() => doc && handleRemoveDocument(doc)}
                        isUploading={isCurrentlyUploading}
                        uploadProgress={isCurrentlyUploading ? uploadingSlot?.progress : undefined}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {/* Other Documents (if any) */}
            {getOtherDocuments().length > 0 && (
              <div>
                <button
                  onClick={() => setShowOtherDocuments(!showOtherDocuments)}
                  className="flex items-center gap-2 text-lg font-semibold text-gray-900 hover:text-gray-700 mb-4"
                >
                  <span>Other Documents</span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                    {getOtherDocuments().length}
                  </span>
                  {showOtherDocuments ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                
                {showOtherDocuments && (
                  <div className="bg-white rounded-xl border border-gray-200 divide-y">
                    {getOtherDocuments().map(doc => (
                      <div key={doc.document_id} className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-gray-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{doc.title || doc.file_name}</p>
                          <p className="text-sm text-gray-500">{doc.document_type}</p>
                        </div>
                        <button
                          onClick={() => handleRemoveDocument(doc)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Info Box */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Why are documents needed?</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Verify your identity and current financial situation</li>
                    <li>• Support hardship or exception requests</li>
                    <li>• Help us create a suitable payment arrangement</li>
                    <li>• Speed up the review and approval process</li>
                  </ul>
                  <p className="text-xs text-blue-600 mt-3">
                    <Sparkles className="w-3 h-3 inline mr-1" />
                    AI-powered verification helps process your documents faster.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
